import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import { Shield, Activity, FileText, Share2, CheckCircle, ClipboardCheck, GitBranch, Lock, MapPin, Building2, BarChart3, Sparkles, CheckSquare, Save, AlertTriangle, RefreshCw, ClipboardList, ChevronDown, ChevronUp, ArrowRight } from 'lucide-react';
import { PublicHeader } from '../components/PublicHeader';
import { PublicFooter } from '../components/PublicFooter';
const FAQS = [{
  question: 'Is this legal advice?',
  answer: 'No. The Legal Health Check is an educational tool that helps you identify areas where you may want to consult a lawyer. It does not replace professional legal advice.'
}, {
  question: 'Is this only for business owners?',
  answer: 'No. The assessment includes a full personal legal health check covering family, estate, property, and more. The business section is optional.'
}, {
  question: 'Can I use it just for personal legal readiness?',
  answer: 'Yes. During onboarding, you can skip the business section entirely and focus only on your personal legal health.'
}, {
  question: 'What happens after I complete the assessment?',
  answer: 'You receive a detailed legal health score across all areas, along with a prioritized action plan with specific next steps.'
}, {
  question: 'Can I download or share my report?',
  answer: 'Yes. You can download a PDF of your full report or share it securely via a unique link with a lawyer, partner, or advisor.'
}, {
  question: 'Do I need to complete everything at once?',
  answer: 'No. Your progress is saved automatically. You can return anytime and pick up where you left off.'
}];
export function HomePage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  // Score card animation
  const scoreCardRef = useRef<HTMLDivElement>(null);
  const scoreInView = useInView(scoreCardRef, {
    once: true,
    margin: '-80px'
  });
  const [animatedScore, setAnimatedScore] = useState(0);
  const [animatedBar1, setAnimatedBar1] = useState(0);
  const [animatedBar2, setAnimatedBar2] = useState(0);
  const [animatedBar3, setAnimatedBar3] = useState(0);
  useEffect(() => {
    if (!scoreInView) return;
    const duration = 1200;
    const steps = 60;
    const interval = duration / steps;
    let step = 0;
    const timer = setInterval(() => {
      step++;
      const progress = step / steps;
      // Ease out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setAnimatedScore(Math.round(72 * eased));
      setAnimatedBar1(Math.round(85 * eased));
      setAnimatedBar2(Math.round(60 * eased));
      setAnimatedBar3(Math.round(35 * eased));
      if (step >= steps) clearInterval(timer);
    }, interval);
    return () => clearInterval(timer);
  }, [scoreInView]);
  // circumference for r=56: 2 * PI * 56 = 351.86
  const circumference = 351.86;
  const scoreOffset = scoreInView ? circumference - circumference * animatedScore / 100 : circumference;
  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };
  const fadeUpVariant = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0
    }
  };
  return <div className="min-h-screen bg-white flex flex-col font-sans" data-id="element-550">
      <PublicHeader data-id="element-551" />

      <main className="flex-grow" data-id="element-552">
        {/* 1. HERO SECTION */}
        <section className="relative bg-aliceBlue pt-20 pb-24 sm:pt-32 sm:pb-32 overflow-hidden" data-id="element-553">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-554">
            <div className="text-center max-w-4xl mx-auto" data-id="element-555">
              <motion.h1 initial="hidden" animate="visible" variants={fadeUpVariant} className="text-4xl sm:text-5xl lg:text-6xl font-bold text-primary tracking-tight mb-6 leading-tight" data-id="element-556">
                Your Legal Health Check for Personal and Business Life
              </motion.h1>
              <motion.p initial="hidden" animate="visible" variants={fadeUpVariant} transition={{
              delay: 0.1
            }} className="text-lg sm:text-xl text-secondary mb-10 max-w-3xl mx-auto" data-id="element-557">
                Spot legal gaps before they become problems. Answer guided
                questions, see your legal health score, and get a practical
                action plan — built for Canadians.
              </motion.p>
              <motion.div initial="hidden" animate="visible" variants={fadeUpVariant} transition={{
              delay: 0.2
            }} className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16" data-id="element-558">
                <Link to="/signup" className="w-full sm:w-auto px-8 py-4 bg-violetBlue text-white text-lg font-semibold rounded-lg hover:opacity-90 transition-opacity shadow-lg shadow-violetBlue/20 flex items-center justify-center" data-id="element-559">
                  Get Started <ArrowRight className="ml-2 w-5 h-5" data-id="element-560" />
                </Link>
                <a href="#how-it-works" className="w-full sm:w-auto px-8 py-4 bg-white text-primary border border-borderColor text-lg font-semibold rounded-lg hover:bg-aliceBlue transition-colors flex items-center justify-center" data-id="element-561">
                  See How It Works
                </a>
              </motion.div>

              {/* Visual Product Preview Mockup */}
              <motion.div initial={{
              opacity: 0,
              y: 40
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.4,
              duration: 0.6
            }} className="max-w-3xl mx-auto bg-white rounded-2xl border border-borderColor shadow-2xl overflow-hidden text-left" data-id="element-562">
                <div className="bg-primary px-6 py-4 flex items-center justify-between" data-id="element-563">
                  <div className="flex items-center gap-2" data-id="element-564">
                    <Activity className="w-5 h-5 text-white" data-id="element-565" />
                    <span className="text-white font-semibold" data-id="element-566">
                      Legal Health Score
                    </span>
                  </div>
                  <span className="text-white/80 text-sm" data-id="element-567">
                    Last updated: Today
                  </span>
                </div>
                <div ref={scoreCardRef} className="p-6 sm:p-8 grid sm:grid-cols-3 gap-8 items-center" data-id="element-568">
                  <div className="text-center sm:col-span-1" data-id="element-569">
                    <div className="relative inline-flex items-center justify-center" data-id="element-570">
                      <svg className="w-32 h-32 transform -rotate-90" data-id="element-571">
                        <circle cx="64" cy="64" r="56" stroke="currentColor" strokeWidth="12" fill="transparent" className="text-aliceBlue" data-id="element-572" />
                        <circle cx="64" cy="64" r="56" stroke="currentColor" strokeWidth="12" fill="transparent" strokeDasharray={circumference} strokeDashoffset={scoreOffset} className="text-violetBlue transition-all duration-1000 ease-out" strokeLinecap="round" data-id="element-573" />
                      </svg>
                      <div className="absolute flex flex-col items-center justify-center" data-id="element-574">
                        <span className="text-3xl font-bold text-primary" data-id="element-575">
                          {animatedScore}
                        </span>
                        <span className="text-xs text-secondary font-medium uppercase tracking-wider" data-id="element-576">
                          / 100
                        </span>
                      </div>
                    </div>
                    <p className="mt-4 font-semibold text-primary" data-id="element-577">
                      Needs Attention
                    </p>
                  </div>
                  <div className="sm:col-span-2 space-y-6" data-id="element-578">
                    <div data-id="element-579">
                      <div className="flex justify-between text-sm mb-2" data-id="element-580">
                        <span className="font-semibold text-primary" data-id="element-581">
                          Family & Estate
                        </span>
                        <span className="text-turquoise font-bold" data-id="element-582">
                          {animatedBar1}/100
                        </span>
                      </div>
                      <div className="w-full bg-aliceBlue rounded-full h-2.5" data-id="element-583">
                        <div className="bg-turquoise h-2.5 rounded-full transition-all duration-1000 ease-out" style={{
                        width: `${animatedBar1}%`
                      }} data-id="element-584"></div>
                      </div>
                    </div>
                    <div data-id="element-585">
                      <div className="flex justify-between text-sm mb-2" data-id="element-586">
                        <span className="font-semibold text-primary" data-id="element-587">
                          Property & Housing
                        </span>
                        <span className="text-yellow-500 font-bold" data-id="element-588">
                          {animatedBar2}/100
                        </span>
                      </div>
                      <div className="w-full bg-aliceBlue rounded-full h-2.5" data-id="element-589">
                        <div className="bg-yellow-500 h-2.5 rounded-full transition-all duration-1000 ease-out" style={{
                        width: `${animatedBar2}%`
                      }} data-id="element-590"></div>
                      </div>
                    </div>
                    <div data-id="element-591">
                      <div className="flex justify-between text-sm mb-2" data-id="element-592">
                        <span className="font-semibold text-primary" data-id="element-593">
                          Business Contracts
                        </span>
                        <span className="text-destructive font-bold" data-id="element-594">
                          {animatedBar3}/100
                        </span>
                      </div>
                      <div className="w-full bg-aliceBlue rounded-full h-2.5" data-id="element-595">
                        <div className="bg-destructive h-2.5 rounded-full transition-all duration-1000 ease-out" style={{
                        width: `${animatedBar3}%`
                      }} data-id="element-596"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* 2. TRUST/VALUE STRIP */}
        <section className="bg-white py-12 border-b border-borderColor" data-id="element-597">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-598">
            <div className="flex flex-wrap justify-center gap-8 md:gap-12 lg:justify-between items-start" data-id="element-599">
              {[{
              icon: ClipboardCheck,
              label: 'Guided Assessment'
            }, {
              icon: FileText,
              label: 'Personalized Action Plan'
            }, {
              icon: GitBranch,
              label: 'Personal & Business Paths'
            }, {
              icon: Lock,
              label: 'Secure PDF Reports'
            }, {
              icon: MapPin,
              label: 'Built for Canada'
            }].map((item, i) => <motion.div key={i} initial="hidden" whileInView="visible" viewport={{
              once: true
            }} variants={fadeUpVariant} transition={{
              delay: i * 0.1
            }} className="flex flex-col items-center text-center max-w-[140px]" data-id="element-600">
                  <div className="w-12 h-12 bg-aliceBlue rounded-full flex items-center justify-center mb-3 text-violetBlue" data-id="element-601">
                    <item.icon className="w-6 h-6" data-id="element-602" />
                  </div>
                  <span className="text-sm font-semibold text-primary leading-tight" data-id="element-603">
                    {item.label}
                  </span>
                </motion.div>)}
            </div>
          </div>
        </section>

        {/* 3. HOW IT WORKS */}
        <section id="how-it-works" className="py-24 bg-aliceBlue" data-id="element-604">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-605">
            <div className="text-center mb-16" data-id="element-606">
              <motion.h2 initial="hidden" whileInView="visible" viewport={{
              once: true
            }} variants={fadeUpVariant} className="text-3xl sm:text-4xl font-bold text-primary mb-4" data-id="element-607">
                How It Works
              </motion.h2>
              <motion.p initial="hidden" whileInView="visible" viewport={{
              once: true
            }} variants={fadeUpVariant} transition={{
              delay: 0.1
            }} className="text-lg text-secondary" data-id="element-608">
                Three simple steps to legal clarity.
              </motion.p>
            </div>

            <div className="grid md:grid-cols-3 gap-12 relative max-w-5xl mx-auto" data-id="element-609">
              <div className="hidden md:block absolute top-12 left-[15%] right-[15%] h-1 bg-borderColor z-0" data-id="element-610"></div>
              {[{
              step: '1',
              title: 'Answer Guided Questions',
              desc: 'Simple, plain-language questions about your family, property, work, and business situation.'
            }, {
              step: '2',
              title: 'See Your Legal Health Score',
              desc: 'Get a clear score across each area so you know exactly where you stand.'
            }, {
              step: '3',
              title: 'Get Your Action Plan',
              desc: 'Receive prioritized, practical next steps to close your legal gaps.'
            }].map((item, i) => <motion.div key={i} initial="hidden" whileInView="visible" viewport={{
              once: true
            }} variants={fadeUpVariant} transition={{
              delay: i * 0.2
            }} className="relative z-10 flex flex-col items-center text-center" data-id="element-611">
                  <div className="w-24 h-24 bg-white border-4 border-aliceBlue rounded-full flex items-center justify-center shadow-md mb-6" data-id="element-612">
                    <span className="text-3xl font-bold text-violetBlue" data-id="element-613">
                      {item.step}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold text-primary mb-3" data-id="element-614">
                    {item.title}
                  </h3>
                  <p className="text-secondary" data-id="element-615">{item.desc}</p>
                </motion.div>)}
            </div>
          </div>
        </section>

        {/* 4. PERSONAL VS BUSINESS COMPARISON */}
        <section className="py-24 bg-white" data-id="element-616">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-617">
            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto" data-id="element-618">
              <motion.div initial="hidden" whileInView="visible" viewport={{
              once: true
            }} variants={fadeUpVariant} className="bg-white border border-borderColor rounded-xl p-8 shadow-sm hover:shadow-md transition-shadow flex flex-col h-full" data-id="element-619">
                <div className="w-14 h-14 bg-aliceBlue rounded-xl flex items-center justify-center mb-6" data-id="element-620">
                  <Shield className="w-7 h-7 text-turquoise" data-id="element-621" />
                </div>
                <h3 className="text-2xl font-bold text-primary mb-4" data-id="element-622">
                  Personal Legal Health Check
                </h3>
                <p className="text-secondary mb-6" data-id="element-623">
                  Protect your family, assets, and future. A strong personal
                  legal foundation ensures your wishes are respected and your
                  loved ones are secure.
                </p>
                <ul className="space-y-3 mb-8 flex-grow" data-id="element-624">
                  {['Family & relationship changes', 'Wills, POA & estate planning', 'Property & housing', 'Work, money & disputes', 'International & special situations', 'Personal legal readiness'].map((item, i) => <li key={i} className="flex items-start" data-id="element-625">
                      <CheckCircle className="w-5 h-5 text-turquoise mr-3 flex-shrink-0 mt-0.5" data-id="element-626" />
                      <span className="text-primary font-medium" data-id="element-627">{item}</span>
                    </li>)}
                </ul>
                <Link to="/signup" className="block w-full text-center px-6 py-3 bg-white border-2 border-borderColor text-primary font-semibold rounded-lg hover:bg-aliceBlue hover:border-turquoise transition-colors" data-id="element-628">
                  Get Started
                </Link>
              </motion.div>

              <motion.div initial="hidden" whileInView="visible" viewport={{
              once: true
            }} variants={fadeUpVariant} transition={{
              delay: 0.2
            }} className="bg-white border border-borderColor rounded-xl p-8 shadow-sm hover:shadow-md transition-shadow flex flex-col h-full" data-id="element-629">
                <div className="w-14 h-14 bg-aliceBlue rounded-xl flex items-center justify-center mb-6" data-id="element-630">
                  <Building2 className="w-7 h-7 text-violetBlue" data-id="element-631" />
                </div>
                <h3 className="text-2xl font-bold text-primary mb-4" data-id="element-632">
                  Business Legal Health Check
                </h3>
                <p className="text-secondary mb-6" data-id="element-633">
                  Shield your business from liability and compliance risks.
                  Ensure your operations, contracts, and team are legally sound.
                </p>
                <ul className="space-y-3 mb-8 flex-grow" data-id="element-634">
                  {['Business setup & ownership', 'Financial commitments & liability', 'Contracts, commercial terms & IP', 'People & workplace risk', 'Privacy, operations & compliance', 'Business legal readiness'].map((item, i) => <li key={i} className="flex items-start" data-id="element-635">
                      <CheckCircle className="w-5 h-5 text-violetBlue mr-3 flex-shrink-0 mt-0.5" data-id="element-636" />
                      <span className="text-primary font-medium" data-id="element-637">{item}</span>
                    </li>)}
                </ul>
                <Link to="/signup" className="block w-full text-center px-6 py-3 bg-white border-2 border-borderColor text-primary font-semibold rounded-lg hover:bg-aliceBlue hover:border-violetBlue transition-colors" data-id="element-638">
                  Get Started
                </Link>
              </motion.div>
            </div>
          </div>
        </section>

        {/* 5. FEATURES SECTION */}
        <section id="features" className="py-24 bg-aliceBlue" data-id="element-639">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-640">
            <div className="text-center mb-16" data-id="element-641">
              <motion.h2 initial="hidden" whileInView="visible" viewport={{
              once: true
            }} variants={fadeUpVariant} className="text-3xl sm:text-4xl font-bold text-primary mb-4" data-id="element-642">
                Built to give you clarity
              </motion.h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-643">
              {[{
              icon: Activity,
              title: 'Guided Legal Checkup',
              desc: 'Walk through a structured assessment covering all critical legal areas.'
            }, {
              icon: GitBranch,
              title: 'Smart Branching Questions',
              desc: 'Questions adapt based on your answers so you only see what is relevant.'
            }, {
              icon: BarChart3,
              title: 'Legal Health Scoring',
              desc: 'Get a clear numerical score for each section and an overall health rating.'
            }, {
              icon: Sparkles,
              title: 'Personalized Recommendations',
              desc: 'Every recommendation is based on your specific answers and situation.'
            }, {
              icon: CheckSquare,
              title: 'Prioritized Action Plan',
              desc: 'See exactly what to do next, ranked by urgency and effort.'
            }, {
              icon: Share2,
              title: 'PDF Reports & Secure Sharing',
              desc: 'Download your report or share it securely with a lawyer or advisor.'
            }, {
              icon: Save,
              title: 'Save & Return Later',
              desc: 'Pick up right where you left off. Your progress is always saved.'
            }].map((feature, i) => <motion.div key={i} initial="hidden" whileInView="visible" viewport={{
              once: true
            }} variants={fadeUpVariant} transition={{
              delay: i * 0.1
            }} className="bg-white border border-borderColor rounded-xl p-6 shadow-sm" data-id="element-644">
                  <div className="w-12 h-12 bg-aliceBlue rounded-lg flex items-center justify-center mb-5" data-id="element-645">
                    <feature.icon className="w-6 h-6 text-violetBlue" data-id="element-646" />
                  </div>
                  <h3 className="text-xl font-bold text-primary mb-2" data-id="element-647">
                    {feature.title}
                  </h3>
                  <p className="text-secondary" data-id="element-648">{feature.desc}</p>
                </motion.div>)}
            </div>
          </div>
        </section>

        {/* 6. WHY USE IT SECTION */}
        <section className="py-24 bg-white" data-id="element-649">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-650">
            <div className="text-center mb-16" data-id="element-651">
              <motion.h2 initial="hidden" whileInView="visible" viewport={{
              once: true
            }} variants={fadeUpVariant} className="text-3xl sm:text-4xl font-bold text-primary mb-4" data-id="element-652">
                Why check your legal health?
              </motion.h2>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto" data-id="element-653">
              {[{
              icon: AlertTriangle,
              title: 'Catch overlooked risks',
              desc: 'Most people do not realize they have legal gaps until something goes wrong. This tool helps you find them early.'
            }, {
              icon: RefreshCw,
              title: 'After life or business changes',
              desc: 'Marriage, separation, a new business, hiring employees — these events create new legal needs.'
            }, {
              icon: ClipboardList,
              title: 'Organize your next steps',
              desc: 'Instead of guessing, get a clear list of what to address and in what order.'
            }, {
              icon: Shield,
              title: 'Stay proactive, not reactive',
              desc: 'Preventive legal planning costs a fraction of what reactive legal problems cost.'
            }].map((item, i) => <motion.div key={i} initial="hidden" whileInView="visible" viewport={{
              once: true
            }} variants={fadeUpVariant} transition={{
              delay: i * 0.1
            }} className="flex items-start gap-4 p-6 rounded-xl hover:bg-aliceBlue transition-colors" data-id="element-654">
                  <div className="w-12 h-12 bg-white border border-borderColor rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm" data-id="element-655">
                    <item.icon className="w-6 h-6 text-violetBlue" data-id="element-656" />
                  </div>
                  <div data-id="element-657">
                    <h3 className="text-xl font-bold text-primary mb-2" data-id="element-658">
                      {item.title}
                    </h3>
                    <p className="text-secondary" data-id="element-659">{item.desc}</p>
                  </div>
                </motion.div>)}
            </div>
          </div>
        </section>

        {/* 7. SAMPLE OUTCOMES SECTION */}
        <section className="py-24 bg-aliceBlue" data-id="element-660">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-661">
            <div className="text-center mb-16" data-id="element-662">
              <motion.h2 initial="hidden" whileInView="visible" viewport={{
              once: true
            }} variants={fadeUpVariant} className="text-3xl sm:text-4xl font-bold text-primary mb-4" data-id="element-663">
                What your results might look like
              </motion.h2>
            </div>

            <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto" data-id="element-664">
              {/* Card 1: Warning */}
              <motion.div initial="hidden" whileInView="visible" viewport={{
              once: true
            }} variants={fadeUpVariant} className="bg-white border-y border-r border-l-4 border-borderColor border-l-yellow-500 rounded-xl p-6 shadow-sm" data-id="element-665">
                <div className="flex justify-between items-start mb-4" data-id="element-666">
                  <div className="w-10 h-10 bg-yellow-50 rounded-full flex items-center justify-center" data-id="element-667">
                    <AlertTriangle className="w-5 h-5 text-yellow-500" data-id="element-668" />
                  </div>
                  <div className="text-right" data-id="element-669">
                    <span className="text-2xl font-bold text-primary" data-id="element-670">45</span>
                    <span className="text-xs text-secondary font-medium" data-id="element-671">
                      /100
                    </span>
                  </div>
                </div>
                <h3 className="text-lg font-bold text-primary mb-2" data-id="element-672">
                  Your estate planning may need attention
                </h3>
                <p className="text-sm text-secondary" data-id="element-673">
                  No Power of Attorney documented. Will may need updating based
                  on recent life changes.
                </p>
              </motion.div>

              {/* Card 2: Critical */}
              <motion.div initial="hidden" whileInView="visible" viewport={{
              once: true
            }} variants={fadeUpVariant} transition={{
              delay: 0.1
            }} className="bg-white border-y border-r border-l-4 border-borderColor border-l-destructive rounded-xl p-6 shadow-sm" data-id="element-674">
                <div className="flex justify-between items-start mb-4" data-id="element-675">
                  <div className="w-10 h-10 bg-red-50 rounded-full flex items-center justify-center" data-id="element-676">
                    <Activity className="w-5 h-5 text-destructive" data-id="element-677" />
                  </div>
                  <div className="text-right" data-id="element-678">
                    <span className="text-2xl font-bold text-primary" data-id="element-679">28</span>
                    <span className="text-xs text-secondary font-medium" data-id="element-680">
                      /100
                    </span>
                  </div>
                </div>
                <h3 className="text-lg font-bold text-primary mb-2" data-id="element-681">
                  Your business contracts need review
                </h3>
                <p className="text-sm text-secondary" data-id="element-682">
                  Contracts reused without legal review. No written vendor
                  agreements in place. High exposure to liability.
                </p>
              </motion.div>

              {/* Card 3: Good */}
              <motion.div initial="hidden" whileInView="visible" viewport={{
              once: true
            }} variants={fadeUpVariant} transition={{
              delay: 0.2
            }} className="bg-white border-y border-r border-l-4 border-borderColor border-l-turquoise rounded-xl p-6 shadow-sm" data-id="element-683">
                <div className="flex justify-between items-start mb-4" data-id="element-684">
                  <div className="w-10 h-10 bg-teal-50 rounded-full flex items-center justify-center" data-id="element-685">
                    <Shield className="w-5 h-5 text-turquoise" data-id="element-686" />
                  </div>
                  <div className="text-right" data-id="element-687">
                    <span className="text-2xl font-bold text-primary" data-id="element-688">88</span>
                    <span className="text-xs text-secondary font-medium" data-id="element-689">
                      /100
                    </span>
                  </div>
                </div>
                <h3 className="text-lg font-bold text-primary mb-2" data-id="element-690">
                  Your personal readiness looks strong
                </h3>
                <p className="text-sm text-secondary" data-id="element-691">
                  Documents organized and accessible. Emergency contacts
                  informed. Core legal protections are in place.
                </p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* 8. FAQ SECTION */}
        <section id="faq" className="py-24 bg-white" data-id="element-692">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-693">
            <div className="text-center mb-12" data-id="element-694">
              <motion.h2 initial="hidden" whileInView="visible" viewport={{
              once: true
            }} variants={fadeUpVariant} className="text-3xl sm:text-4xl font-bold text-primary mb-4" data-id="element-695">
                Frequently Asked Questions
              </motion.h2>
            </div>

            <div className="space-y-4" data-id="element-696">
              {FAQS.map((faq, index) => <motion.div key={index} initial="hidden" whileInView="visible" viewport={{
              once: true
            }} variants={fadeUpVariant} transition={{
              delay: index * 0.1
            }} className="border border-borderColor rounded-xl overflow-hidden bg-white" data-id="element-697">
                  <button onClick={() => toggleFaq(index)} className="w-full px-6 py-5 text-left flex justify-between items-center focus:outline-none hover:bg-aliceBlue transition-colors" data-id="element-698">
                    <span className="font-semibold text-primary pr-4" data-id="element-699">
                      {faq.question}
                    </span>
                    {openFaq === index ? <ChevronUp className="w-5 h-5 text-secondary flex-shrink-0" data-id="element-700" /> : <ChevronDown className="w-5 h-5 text-secondary flex-shrink-0" data-id="element-701" />}
                  </button>
                  <AnimatePresence data-id="element-702">
                    {openFaq === index && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.2
                }} data-id="element-703">
                        <div className="px-6 pb-5 text-secondary border-t border-borderColor pt-4" data-id="element-704">
                          {faq.answer}
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </motion.div>)}
            </div>
          </div>
        </section>

        {/* 9. FINAL CTA SECTION */}
        <section className="py-24 bg-primary" data-id="element-705">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-706">
            <motion.h2 initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUpVariant} className="text-3xl sm:text-4xl font-bold text-white mb-6" data-id="element-707">
              Take control of your legal readiness
            </motion.h2>
            <motion.p initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUpVariant} transition={{
            delay: 0.1
          }} className="text-lg text-white/80 mb-10" data-id="element-708">
              It takes less than 10 minutes. No credit card required.
            </motion.p>
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUpVariant} transition={{
            delay: 0.2
          }} data-id="element-709">
              <Link to="/signup" className="inline-block px-10 py-4 bg-white text-primary text-lg font-semibold rounded-lg hover:opacity-90 transition-opacity shadow-lg" data-id="element-710">
                Create Your Account
              </Link>
            </motion.div>
          </div>
        </section>
      </main>

      {/* 10. FOOTER */}
      <PublicFooter data-id="element-711" />
    </div>;
}